import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import { types as sdkTypes } from '../util/sdkLoader';
import Decimal from 'decimal.js';

// Utility functions for managing cart items in sessionStorage

export const CART_STORAGE_KEY = 'rentAnything_cart';
export const BILLING_INFO_STORAGE_KEY = 'rentAnything_billingInfo';

export const getCartFromSession = () => {
  if (typeof window === 'undefined') return null;
  const cartData = window.sessionStorage.getItem(CART_STORAGE_KEY);
  return cartData ? JSON.parse(cartData) : null;
};

export const saveCartToSession = (cartData) => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartData));
};

export const clearCartFromSession = () => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(CART_STORAGE_KEY);
};

export const addItemToSessionStorageCart = (params) => {
  try {
    const { authorId, listingId, bookingDates, quantity = 1, timeZone, units = 1, selectedPackage, usageMultipleValues, selectedFeeTypes, otherOptionalFees, pickupAtOwnerLocation, returnAtOwnerLocation, queryID } = params;
    const resolvedSelectedFeeTypes = selectedFeeTypes || otherOptionalFees;
    const { startDate, endDate } = bookingDates || {};
    if (
      !selectedPackage ||
      !startDate ||
      !endDate ||
      !listingId ||
      !authorId ||
      !timeZone ||
      !units
    ) {
      throw new Error('Required data is missing');
    }

    const cart = getCartFromSession() || [];

    const existingItemIndex = cart.findIndex(i => {
      const { startDate, endDate } = i.bookingDates;
      const { startDate: currentStart, endDate: currentEnd } = bookingDates;

      return (
        i.listingId === listingId &&
        moment(startDate).isSame(currentStart) &&
        moment(endDate).isSame(currentEnd) &&
        i.package === selectedPackage
      );
    });

    let updatedCart = [...cart];

    if (existingItemIndex !== -1) {
      updatedCart[existingItemIndex] = {
        ...updatedCart[existingItemIndex],
        quantity: Number(updatedCart[existingItemIndex].quantity || 1) + Number(quantity),
        ...(queryID && { queryID }),
        // Update optional fields if provided
        ...(usageMultipleValues !== undefined && { usageMultipleValues }),
        ...(resolvedSelectedFeeTypes !== undefined && { selectedFeeTypes: resolvedSelectedFeeTypes }),
        ...(pickupAtOwnerLocation !== undefined && { pickupAtOwnerLocation }),
        ...(returnAtOwnerLocation !== undefined && { returnAtOwnerLocation }),
      };
    } else {
      updatedCart.push({
        id: uuidv4(),
        authorId,
        listingId,
        package: selectedPackage,
        bookingDates,
        quantity: Number(quantity),
        timeZone,
        units: Number(units),
        ...(queryID && { queryID }),
        usageMultipleValues,
        selectedFeeTypes: resolvedSelectedFeeTypes,
        pickupAtOwnerLocation,
        returnAtOwnerLocation,
        addedAt: new Date().toISOString(), // Timestamp for sorting (latest first)
      });
    }

    saveCartToSession(updatedCart);
    return updatedCart;
  } catch (error) {
    console.error('Error adding item to cart:', error);
    throw error;
  }
};

export const removeItemFromSessionStorageCart = (itemId) => {
  const cart = getCartFromSession() || [];
  const updatedCart = cart.filter(item => item.id !== itemId);
  saveCartToSession(updatedCart);
  return updatedCart;
};

export const saveBillingInfoToSession = (billingInfo) => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(BILLING_INFO_STORAGE_KEY, JSON.stringify(billingInfo));
};


export const getBillingInfoFromSession = () => {
  if (typeof window === 'undefined') return null;
  const billingInfo = window.sessionStorage.getItem(BILLING_INFO_STORAGE_KEY);
  return billingInfo ? JSON.parse(billingInfo) : null;
};

export const clearBillingInfoFromSession = () => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(BILLING_INFO_STORAGE_KEY);
};

// Checkout context storage for post-signup redirect
export const CHECKOUT_CONTEXT_STORAGE_KEY = 'rentAnything_checkoutContext';

export const saveCheckoutContextToSession = (checkoutContext) => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(CHECKOUT_CONTEXT_STORAGE_KEY, JSON.stringify(checkoutContext));
};

export const getCheckoutContextFromSession = () => {
  if (typeof window === 'undefined') return null;
  const checkoutContext = window.sessionStorage.getItem(CHECKOUT_CONTEXT_STORAGE_KEY);
  return checkoutContext ? JSON.parse(checkoutContext) : null;
};

export const clearCheckoutContextFromSession = () => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(CHECKOUT_CONTEXT_STORAGE_KEY);
};

// Signup data storage during checkout flow
export const SIGNUP_DATA_STORAGE_KEY = 'rentAnything_signupData';

export const saveSignupDataToSession = (signupData) => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(SIGNUP_DATA_STORAGE_KEY, JSON.stringify(signupData));
};

export const getSignupDataFromSession = () => {
  if (typeof window === 'undefined') return null;
  const signupData = window.sessionStorage.getItem(SIGNUP_DATA_STORAGE_KEY);
  return signupData ? JSON.parse(signupData) : null;
};

export const clearSignupDataFromSession = () => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(SIGNUP_DATA_STORAGE_KEY);
};

// Session-only storage for "Rent More Items" redirect URLs
// Stores the page URL user was on when adding to cart (per author)
export const RENT_MORE_URL_KEY = 'rentAnything_rentMoreUrls';

/**
 * Save the URL to redirect to for "Rent More Items" button
 * @param {string} authorId - Author/store owner ID
 * @param {string} url - URL to save (search page or store page)
 */
export const saveRentMoreUrl = (authorId, url) => {
  if (typeof window === 'undefined' || !authorId || !url) return;
  try {
    const data = getRentMoreUrls();
    data[authorId] = url;
    window.sessionStorage.setItem(RENT_MORE_URL_KEY, JSON.stringify(data));
  } catch (e) {
    // sessionStorage might be disabled or full
    console.warn('Failed to save rent more URL:', e);
  }
};

/**
 * Get all saved "Rent More" URLs
 * @returns {Object} Map of authorId -> URL
 */
export const getRentMoreUrls = () => {
  if (typeof window === 'undefined') return {};
  try {
    const data = window.sessionStorage.getItem(RENT_MORE_URL_KEY);
    return data ? JSON.parse(data) : {};
  } catch (e) {
    return {};
  }
};

/**
 * Get the "Rent More" URL for a specific author
 * @param {string} authorId - Author/store owner ID
 * @returns {string|null} Saved URL or null
 */
export const getRentMoreUrl = (authorId) => {
  if (!authorId) return null;
  return getRentMoreUrls()[authorId] || null;
};

/**
 * Clear all saved "Rent More" URLs
 */
export const clearRentMoreUrls = () => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(RENT_MORE_URL_KEY);
  } catch (e) {
    // Ignore errors
  }
};

// ============================================
// Stores added to the cart in THIS browser session
// ============================================

// RAS-2622 follow-up (client clarification 2026-09-18): the "different owner" popup
// is about a cart the shopper started just now - add from store A, keep browsing,
// then Rent Now on store B - not about a cart left over from a previous visit. Every
// successful add-to-cart records its store here (cart.duck.js's addToCart, the one
// write path every surface goes through); the gate then only counts stores in this
// list. sessionStorage on purpose: same "this session" notion as the Rent More URLs
// above, gone when the tab closes, never shared across tabs.
export const CART_AUTHORS_THIS_SESSION_KEY = 'rentAnything_cartAuthorsThisSession';

/**
 * @returns {Array<string>} author ids added to the cart in this browser session
 */
export const getCartAuthorsAddedThisSession = () => {
  if (typeof window === 'undefined') return [];
  try {
    const data = window.sessionStorage.getItem(CART_AUTHORS_THIS_SESSION_KEY);
    const parsed = data ? JSON.parse(data) : [];
    return Array.isArray(parsed) ? parsed.filter(id => typeof id === 'string' && id) : [];
  } catch (e) {
    return [];
  }
};

/**
 * @param {string} authorId - store whose cart just received an item
 */
export const markCartAuthorAddedThisSession = authorId => {
  if (typeof window === 'undefined' || !authorId) return;
  try {
    const ids = getCartAuthorsAddedThisSession();
    if (ids.includes(authorId)) return;
    window.sessionStorage.setItem(CART_AUTHORS_THIS_SESSION_KEY, JSON.stringify([...ids, authorId]));
  } catch (e) {
    // sessionStorage might be disabled or full - the popup simply won't show.
  }
};

// ============================================
// Abandoned Checkout & Mini Cart Dismissal
// ============================================

export const MINI_CART_DISMISSED_KEY = 'rentAnything_miniCartDismissed';
export const ABANDONED_CHECKOUT_KEY = 'rentAnything_abandonedCheckout';
// Versioned key to handle migration from old format
export const ABANDONED_CHECKOUT_KEY_V2 = 'rentAnything_abandonedCheckout_v2';

/**
 * Migrate from old storage key to new versioned key
 * Clears old data that was stored without proper serialization
 */
const migrateAbandonedCheckoutData = () => {
  if (typeof window === 'undefined') return;
  try {
    // Check if old key exists
    const oldData = window.localStorage.getItem(ABANDONED_CHECKOUT_KEY);
    if (oldData) {
      // Clear old data - it's in incompatible format
      window.localStorage.removeItem(ABANDONED_CHECKOUT_KEY);
      console.log('Cleared old abandoned checkout data (migrated to v2)');
    }
  } catch (e) {
    // Silent fail - migration is non-critical
  }
};

// Run migration on module load
if (typeof window !== 'undefined') {
  migrateAbandonedCheckoutData();
}

/**
 * Mark mini cart as dismissed for a specific checkout signature.
 * Uses localStorage so the popup does not reappear when the user returns to the
 * home page (e.g. after closing the modal or clicking "Complete Payment").
 * @param {string} checkoutSignature - Unique signature identifying the checkout
 */
export const setMiniCartDismissed = (checkoutSignature) => {
  if (typeof window === 'undefined' || !checkoutSignature) return;
  try {
    const dismissedData = {
      signature: checkoutSignature,
      dismissed: true,
    };
    window.localStorage.setItem(MINI_CART_DISMISSED_KEY, JSON.stringify(dismissedData));
  } catch (e) {
    // Silent fail
  }
};

/**
 * Check if mini cart was dismissed for a specific checkout signature
 * @param {string} checkoutSignature - Unique signature identifying the checkout
 * @returns {boolean}
 */
export const isMiniCartDismissed = (checkoutSignature) => {
  if (typeof window === 'undefined' || !checkoutSignature) return false;
  try {
    const stored = window.localStorage.getItem(MINI_CART_DISMISSED_KEY);
    if (!stored) return false;

    const dismissedData = JSON.parse(stored);
    // Only dismissed if signature matches (same checkout)
    return dismissedData.signature === checkoutSignature && dismissedData.dismissed === true;
  } catch (e) {
    return false;
  }
};

/**
 * Clear mini cart dismissed state (e.g., when new checkout is started)
 */
export const clearMiniCartDismissed = () => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(MINI_CART_DISMISSED_KEY);
  } catch (e) {
    // Ignore errors
  }
};

/**
 * Save abandoned checkout data to localStorage (persists across sessions)
 * @param {Object} checkoutData - { listing, orderData, lineItems, withinNinetyDays, returnCareInstructions, authorId }
 */
export const saveAbandonedCheckout = (checkoutData) => {
  if (typeof window === 'undefined') return;
  try {
    const data = {
      ...checkoutData,
      abandonedAt: new Date().toISOString(),
    };

    // Use the same replacer pattern as CheckoutPageSessionHelpers to properly serialize Money, UUID, Date, Decimal
    const replacer = function (k, v) {
      if (this[k] instanceof Date) {
        return { date: v, _serializedType: 'SerializableDate' };
      }
      if (this[k] instanceof Decimal) {
        return { decimal: v, _serializedType: 'SerializableDecimal' };
      }
      return sdkTypes.replacer(k, v);
    };

    // console.log('JSON.stringify(data, replacer)', JSON.stringify(data, replacer));
    // Use versioned key
    window.localStorage.setItem(ABANDONED_CHECKOUT_KEY_V2, JSON.stringify(data, replacer));
  } catch (e) {
    // Silent fail for storage errors
  }
};

/**
 * Get abandoned checkout data from localStorage
 * @returns {Object|null} Abandoned checkout data or null
 */
export const getAbandonedCheckout = () => {
  if (typeof window === 'undefined') return null;
  try {
    // Use versioned key
    const rawData = window.localStorage.getItem(ABANDONED_CHECKOUT_KEY_V2);
    if (!rawData) return null;

    // Use the same reviver pattern as CheckoutPageSessionHelpers to properly deserialize Money, UUID, Date, Decimal
    const reviver = (k, v) => {
      if (v && typeof v === 'object' && v._serializedType === 'SerializableDate') {
        // Dates are expected to be stored as:
        // { date: new Date(), _serializedType: 'SerializableDate' }
        return new Date(v.date);
      } else if (v && typeof v === 'object' && v._serializedType === 'SerializableDecimal') {
        // Decimals are expected to be stored as:
        // { decimal: v, _serializedType: 'SerializableDecimal' }
        return new Decimal(v.decimal);
      }
      return sdkTypes.reviver(k, v);
    };

    const parsed = JSON.parse(rawData, reviver);
    // console.log('parsed', parsed);
    // Check if data is fresh (max 7 days old for localStorage)
    const abandonedAt = parsed.abandonedAt ? new Date(parsed.abandonedAt) : null;
    if (!abandonedAt) {
      // If abandonedAt is missing or invalid, clear and return null
      clearAbandonedCheckout();
      return null;
    }

    const now = new Date();
    const daysDiff = (now - abandonedAt) / (1000 * 60 * 60 * 24);

    if (daysDiff > 7) {
      clearAbandonedCheckout();
      return null;
    }

    return parsed;
  } catch (e) {
    // If parsing fails, clear corrupted data
    clearAbandonedCheckout();
    return null;
  }
};

/**
 * Clear abandoned checkout data from localStorage
 */
export const clearAbandonedCheckout = () => {
  if (typeof window === 'undefined') return;
  try {
    // Clear both old and new keys to be safe
    window.localStorage.removeItem(ABANDONED_CHECKOUT_KEY);
    window.localStorage.removeItem(ABANDONED_CHECKOUT_KEY_V2);
  } catch (e) {
    // Ignore errors
  }
};

/**
 * Check if there's an active abandoned checkout
 * @returns {boolean}
 */
export const hasAbandonedCheckout = () => {
  return getAbandonedCheckout() !== null;
};

// ---------------------------------------------------------------------------
// Payment failure reason
//
// When a checkout attempt fails at a step after the transaction has been
// created, we remember why (a short cause code) keyed by transaction id. If
// that transaction then hits `payment-expired`, the TransactionPage can show
// the real cause instead of a generic "not confirmed in time" line. Best-effort
// and same-device only — the honest generic copy is always shown regardless, so
// a miss (cleared storage, other device) just means no cause line.
// ---------------------------------------------------------------------------
export const PAYMENT_FAILURE_REASON_KEY = 'rentAnything_paymentFailureReason';
const PAYMENT_FAILURE_REASON_TTL_MS = 24 * 60 * 60 * 1000;
export const PAYMENT_FAILURE_REASONS = [
  'authentication',
  'cardNotAccepted',
  'declined',
  'connection',
  'chargedNotConfirmed',
  'locked',
];

export const savePaymentFailureReason = (transactionId, code) => {
  if (typeof window === 'undefined' || !transactionId || !PAYMENT_FAILURE_REASONS.includes(code)) {
    return;
  }
  try {
    const now = Date.now();
    const raw = window.localStorage.getItem(PAYMENT_FAILURE_REASON_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const pruned = Object.fromEntries(
      Object.entries(parsed).filter(([, v]) => v && now - v.ts < PAYMENT_FAILURE_REASON_TTL_MS)
    );
    pruned[transactionId] = { code, ts: now };
    window.localStorage.setItem(PAYMENT_FAILURE_REASON_KEY, JSON.stringify(pruned));
  } catch (e) {
    // sessionStorage/localStorage may be unavailable or full — non-fatal.
  }
};

export const getPaymentFailureReason = transactionId => {
  if (typeof window === 'undefined' || !transactionId) return null;
  try {
    const raw = window.localStorage.getItem(PAYMENT_FAILURE_REASON_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw)[transactionId];
    if (!entry || Date.now() - entry.ts >= PAYMENT_FAILURE_REASON_TTL_MS) return null;
    return PAYMENT_FAILURE_REASONS.includes(entry.code) ? entry.code : null;
  } catch (e) {
    return null;
  }
};
